import { useEffect, useState } from 'react';
import { api } from './api';
import { dataHora, rotulo } from './formato';

/**
 * A trilha de auditoria de UMA ficha, como bloco de tela (UX-010).
 *
 * A API já servia `GET /os/:id/trilha` e `GET /amostras/:id/trilha` desde
 * a `vw_trilha` — e nenhuma ficha as chamava. O §11 do briefing pede que
 * o sistema permita identificar quem alterou, o que e quando; a OS
 * respondia isso só a quem abrisse o banco. A ficha do equipamento já
 * tinha o bloco (CA-21); este é o mesmo desenho, para qualquer ficha.
 *
 * Só aparece para quem tem `auditoria.consultar`: o 403 de quem não tem
 * é o `catch` silencioso, e não um erro na tela. Um `INSERT` é «cadastro»
 * e as alterações mostram campo, valor antigo e valor novo — e o motivo,
 * quando quem alterou escreveu um.
 */
export interface Elo {
  operacao: string; datahora: string;
  usuario_nome: string | null; usuario_email: string | null;
  perfil: string | null; motivo: string | null;
  campos_alterados: string[] | null;
  valor_anterior: Record<string, unknown> | null;
  valor_novo: Record<string, unknown> | null;
}

/** Mostra um valor da trilha sem despejar JSON na tela. */
function valor(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'sim' : 'não';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) return dataHora(v);
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

export function TrilhaDaFicha({
  rota, titulo = 'Histórico da ficha', explica, ocultar = [],
}: {
  /** O caminho da API que devolve os elos — `/os/:id/trilha`. */
  rota: string;
  titulo?: string;
  explica?: string;
  /** Campos técnicos que não interessam a quem lê (carimbos internos). */
  ocultar?: string[];
}) {
  const [trilha, setTrilha] = useState<Elo[] | null>(null);

  useEffect(() => {
    const abortar = new AbortController();
    api<Elo[]>(rota, { sinal: abortar.signal })
      .then(setTrilha)
      .catch(() => setTrilha(null));
    return () => abortar.abort();
  }, [rota]);

  if (!trilha || trilha.length === 0) return null;
  const escondido = new Set(ocultar);

  return (
    <div className="cartao trilha-da-ficha">
      <details>
        <summary>
          <b>{titulo}</b>
          <span className="sub"> · {trilha.length} registro(s)</span>
        </summary>
        {explica && <p className="sub" style={{ marginTop: '.6rem' }}>{explica}</p>}
        <div className="rola">
          <table className="dados">
            <thead>
              <tr>
                <th>Quando</th><th>Quem</th><th>O que mudou</th><th>Motivo</th>
              </tr>
            </thead>
            <tbody>
              {trilha.map((e, i) => {
                const campos = (e.campos_alterados ?? [])
                  .filter((c) => !escondido.has(c));
                return (
                  <tr key={`${e.datahora}-${i}`}>
                    <td>{dataHora(e.datahora)}</td>
                    <td>
                      {e.usuario_nome ?? e.usuario_email
                        ?? <span className="nada">sistema</span>}
                      {e.perfil && <div className="sub">{rotulo(e.perfil)}</div>}
                    </td>
                    <td>
                      {e.operacao === 'INSERT'
                        ? <span className="sub">cadastro</span>
                        : e.operacao === 'DELETE'
                          ? <span className="sub">exclusão</span>
                          : campos.length === 0
                            ? <span className="nada">—</span>
                            : campos.map((c) => (
                              <div key={c}>
                                <b>{rotulo(c)}</b>
                                {': '}
                                <span className="mono">{valor(e.valor_anterior?.[c])}</span>
                                {' → '}
                                <span className="mono">{valor(e.valor_novo?.[c])}</span>
                              </div>
                            ))}
                    </td>
                    <td className="sub">
                      {e.motivo ?? <span className="nada">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}
